import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Show } from "@clerk/react";
import { toast } from "sonner";
import { useT, useSeo } from "@/lib/i18n";
import { useApi, pub } from "@/lib/api";
import { PageHead, HaloCard, CtaLink, ShareBar } from "@/components/bits";
import content from "@/interactive-content.json";

const CARDS = content.cards.items;
const Q = content.quiz.questions;
const RESULTS = content.quiz.results;
const ss = { get: (k) => { try { return JSON.parse(sessionStorage.getItem(k)); } catch { return null; } }, set: (k, v) => sessionStorage.setItem(k, JSON.stringify(v)), del: (k) => sessionStorage.removeItem(k) };
const randInt = (n) => { const a = new Uint32Array(1); crypto.getRandomValues(a); return a[0] % n; };

function SaveButton({ type, refId, answers, label }) {
  const { locale, tl, t } = useT();
  const api = useApi();
  const [saved, setSaved] = useState(false);
  const [consent, setConsent] = useState(type !== "quiz");
  const save = async () => { try { await api.post("/reflections/save", { type, ref_id: refId, answers, locale }); setSaved(true); toast.success(tl("controls")[0]); } catch { toast.error(tl("errors")[5]); } };
  return (
    <>
      <Show when="signed-in">
        {type === "quiz" && !saved && <label className="flex gap-2 text-sm"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="quiz-save-consent" />{t("quizSave")}</label>}
        <button className="btn btn-ghost btn-sm" disabled={saved || !consent} onClick={save} data-testid={`save-${type}`}>{saved ? tl("controls")[0] : label}</button>
      </Show>
      <Show when="signed-out"><Link to="/sign-in" className="btn btn-ghost btn-sm" data-testid={`save-${type}-signin`}>{tl("tarotCtas")[2]}</Link></Show>
    </>
  );
}

export function LoveTarot() {
  const { t, tl, pick, lp } = useT();
  useSeo("love");
  const [pickedId, setPickedId] = useState(() => ss.get("aa_card"));
  const [deck, setDeck] = useState(0);
  const card = CARDS.find((c) => c.id === pickedId);
  const R = tl("resultLabels"), C = tl("tarotCtas");
  const choose = () => { const c = CARDS[randInt(CARDS.length)]; ss.set("aa_card", c.id); setPickedId(c.id); };
  const again = () => { ss.del("aa_card"); setPickedId(null); setDeck(deck + 1); };
  return (
    <>
      <PageHead title={t("tarotH1")} lead={t("tarotIntro")} />
      <section className="wrap pb-20">
        <p className="notice text-sm mb-10" data-testid="cards-label">{t("cardsLabel")}</p>
        {!card ? (
          <div><p className="serif text-2xl mb-6">{t("breathe")}</p>
            <div className="grid grid-cols-3 gap-4 md:gap-8 max-w-[760px]" key={deck}>
              {[1, 2, 3].map((n) => (
                <HaloCard key={n} className="rounded-[20px]"><button onClick={choose} aria-label={t("faceDown", { n })} data-testid={`tarot-card-${n}`}
                  className="w-full aspect-[2/3] rounded-[20px] bg-[var(--forest)] border border-[var(--gold)] grid place-items-center overflow-hidden">
                  <img src="/assets/mandala.svg" alt="" aria-hidden="true" className="w-3/4 opacity-60" /></button></HaloCard>))}
            </div>
            <button className="btn btn-gold mt-8" onClick={choose} data-testid="tarot-turn">{t("tarotCta")}</button>
          </div>
        ) : (
          <article className="grid md:grid-cols-[320px_1fr] gap-10 reveal" data-testid="tarot-result" aria-live="polite">
            <div className="aspect-[2/3] rounded-[20px] bg-[var(--paper)] border-2 border-[var(--gold)] grid place-items-center p-6 text-center">
              <img src="/assets/logo-main-192.png" alt="" aria-hidden="true" className="w-20 mb-4" /><p className="eyebrow">{card.id}</p><h2 className="text-4xl" data-testid="tarot-card-title">{pick(card.content)?.title}</h2></div>
            <div className="grid gap-5 content-start">
              <div><p className="eyebrow">{R[0]}</p><p className="text-xl">{pick(card.content).reflection}</p></div>
              <div><p className="eyebrow">{R[1]}</p><p className="serif text-2xl">{pick(card.content).question}</p></div>
              <div><p className="eyebrow">{R[2]}</p><p>{pick(card.content).action}</p></div>
              <p className="text-sm italic text-[var(--muted)]">{t("notPrediction")}</p>
              <div className="flex flex-wrap gap-2"><SaveButton type="card" refId={card.id} label={R[3]} /><button className="btn btn-ghost btn-sm" onClick={again} data-testid="tarot-again">{R[5]}</button></div>
              <ShareBar path="/love-tarot" text={`“${pick(card.content).question}” — Ayahuasca Angel`} />
              <p className="mt-4">{t("tarotBridge")}</p>
              <div className="flex flex-wrap gap-3"><CtaLink to={lp("/book")} variant="forest">{C[0]}</CtaLink><CtaLink to={lp("/oracle")} variant="ghost">{C[1]}</CtaLink></div>
            </div>
          </article>)}
      </section>
    </>
  );
}

function score(answers) {
  const totals = { ROOT: 0, VOICE: 0, BOUNDARY: 0, RELEASE: 0, OPEN: 0 }, prim = [];
  Q.forEach((q, i) => { const o = q.options.find((x) => x.id === answers[i]); Object.entries(o.scores).forEach(([k, v]) => { totals[k] += v; }); prim.push(o.primary); });
  const top = Math.max(...Object.values(totals));
  const tied = Object.keys(totals).filter((k) => totals[k] === top);
  for (let i = prim.length - 1; i >= 0; i--) if (tied.includes(prim[i])) return prim[i];
  return content.quiz.scoring.fallbackOrder.find((k) => tied.includes(k));
}

export function LoveOracle() {
  const { t, tl, pick, lp } = useT();
  useSeo("love");
  const nav = useNavigate();
  const { pathname } = useLocation();
  const isResult = pathname.endsWith("/result");
  const [answers, setAnswers] = useState(() => ss.get("aa_quiz") || Array(6).fill(null));
  const [idx, setIdx] = useState(0);
  useEffect(() => { ss.set("aa_quiz", answers); }, [answers]);
  const C = tl("quizControls"), CT = tl("quizCtas");
  const complete = answers.every(Boolean);
  const resultId = complete ? score(answers) : null;
  useEffect(() => { if (isResult && !complete) nav(lp("/love-oracle"), { replace: true }); }, [isResult, complete, nav, lp]);
  const reset = () => { ss.del("aa_quiz"); setAnswers(Array(6).fill(null)); setIdx(0); nav(lp("/love-oracle")); };
  if (isResult && resultId) {
    const r = pick(RESULTS.find((x) => x.id === resultId).content);
    return (
      <>
        <PageHead eyebrow={t("basedOn")} title={r.title} />
        <section className="wrap pb-20 grid gap-6 max-w-[760px]" data-testid="quiz-result" data-result={resultId}>
          <p className="text-xl">{r.body}</p>
          <div className="card p-6"><p className="eyebrow">{tl("resultLabels")[1]}</p><p className="serif text-2xl">{r.question}</p><p className="eyebrow mt-4">{tl("resultLabels")[2]}</p><p>{r.action}</p></div>
          <details className="card p-5"><summary className="font-semibold cursor-pointer" data-testid="quiz-how">{t("howChosen")}</summary><p className="mt-3 text-sm">{pick(content.quiz.scoring.plainExplanation)}</p></details>
          <div className="flex flex-wrap gap-2"><SaveButton type="quiz" refId={resultId} answers={answers} label={CT[2]} /><button className="btn btn-ghost btn-sm" onClick={reset} data-testid="quiz-restart">{C[4]}</button></div>
          <ShareBar path="/love-oracle" text={`${r.title} — Ayahuasca Angel`} />
          <p>{t("quizBridge")}</p>
          <div className="flex flex-wrap gap-3"><CtaLink to={lp("/book")} variant="forest">{CT[0]}</CtaLink><CtaLink to={lp("/oracle")} variant="ghost">{CT[1]}</CtaLink></div>
        </section>
      </>
    );
  }
  const q = Q[idx];
  return (
    <>
      <PageHead title={t("quizH1")} lead={t("quizIntro")} />
      <section className="wrap pb-20 max-w-[760px]">
        <p className="eyebrow" aria-live="polite" data-testid="quiz-progress">{C[0].replace("{current}", idx + 1)}</p>
        <div className="h-1 bg-[var(--line)] rounded mb-8"><div className="h-1 bg-[var(--gold)] rounded transition-[width]" style={{ width: `${((idx + 1) / 6) * 100}%` }} /></div>
        <fieldset data-testid={`quiz-q${idx + 1}`}><legend className="serif text-3xl md:text-4xl mb-6">{pick(q.title)}</legend>
          <div className="grid gap-3">
            {q.options.map((o) => (
              <label key={o.id} className={`card p-5 flex gap-3 cursor-pointer ${answers[idx] === o.id ? "ring-2 ring-[var(--gold)]" : ""}`}>
                <input type="radio" name={q.id} value={o.id} checked={answers[idx] === o.id} onChange={() => { const a = [...answers]; a[idx] = o.id; setAnswers(a); }} data-testid={`quiz-opt-${o.id}`} />{pick(o.label)}</label>))}
          </div></fieldset>
        <div className="flex gap-3 mt-8">
          <button className="btn btn-ghost" disabled={idx === 0} onClick={() => setIdx(idx - 1)} data-testid="quiz-back">{C[1]}</button>
          {idx < 5 ? <button className="btn btn-forest" disabled={!answers[idx]} onClick={() => setIdx(idx + 1)} data-testid="quiz-next">{C[2]}</button>
            : <button className="btn btn-gold" disabled={!complete} onClick={() => nav(lp("/love-oracle/result"))} data-testid="quiz-see-result">{C[3]}</button>}
        </div>
        <p className="help mt-6">{C[5]}</p>
      </section>
    </>
  );
}

export { pub };
